import React, { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import './Map.scss';
import { roundToFour } from '../../app/utils/utils';
const domtoimage = require('dom-to-image');

// Type definitions for Leaflet
interface LeafletLatLng {
    lat: number;
    lng: number;
}

interface LeafletMouseEvent {
    latlng: LeafletLatLng;
    layerPoint: { x: number; y: number };
    containerPoint: { x: number; y: number };
    originalEvent: MouseEvent;
}

interface LeafletMarker {
    setLatLng: (latlng: [number, number]) => LeafletMarker;
    addTo: (map: LeafletMap) => LeafletMarker;
    remove: () => void;
}

interface LeafletMap {
    setView: (center: [number, number], zoom: number, options?: { animate?: boolean }) => LeafletMap;
    on: (event: string, handler: (e: LeafletMouseEvent) => void) => LeafletMap;
    off: (event: string, handler: (e: LeafletMouseEvent) => void) => LeafletMap;
    remove: () => void;
    doubleClickZoom: {
        disable: () => void;
        enable: () => void;
    };
}

interface LeafletTileLayer {
    addTo: (map: LeafletMap) => LeafletTileLayer;
}

interface LeafletGlobal {
    map: (element: HTMLElement) => LeafletMap;
    marker: (latlng: [number, number]) => LeafletMarker;
    tileLayer: (
        url: string,
        options: {
            maxZoom: number;
            attribution: string;
        }
    ) => LeafletTileLayer;
}

declare global {
    interface Window {
        L: LeafletGlobal;
    }
}

export interface ICoordinate {
    latitude: number;
    longitude: number;
}

export interface IMapMethods {
    setCoordinates: (coords: ICoordinate) => void;
}

export interface IMapProps {
    /** Invece di un valore iniziale, ora la mappa riceve le coordinate correnti */
    coordinates: ICoordinate;
    onMapClick?: (coords: ICoordinate) => void;
    onImageUpdate?: (imageDataUrl: string) => void;
}

const Map = forwardRef<IMapMethods, IMapProps>((props, ref) => {
    const { coordinates, onMapClick, onImageUpdate } = props;
    const divMap = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<LeafletMap | null>(null);
    const markerRef = useRef<LeafletMarker | null>(null);

    // Se desideri ancora esporre metodi via ref, li mantieni;
    // altrimenti puoi ometterlo poich� la mappa � controllata tramite props.
    useImperativeHandle(ref, () => ({
        setCoordinates: () => {
            // In questo approccio il metodo non � strettamente necessario,
            // perch� il componente padre deve aggiornare le coordinate e
            // passarle via props.
        },
    }));

    const getImgFromMap = useCallback(() => {
        const mapNode = document.getElementById('map');
        if (!mapNode) return;

        domtoimage
            .toPng(mapNode, { height: 324, width: 418 })
            .then((dataUrl: string) => {
                localStorage.setItem('map', dataUrl);
                onImageUpdate?.(dataUrl);
            })
            .catch((error: unknown) => {
                console.error('Map: DomToImage error:', error);
            });
    }, [onImageUpdate]);

    // Inizializzazione della mappa (solo una volta)
    useEffect(() => {
        if (!divMap.current) return;
        mapRef.current = window.L.map(divMap.current).setView([coordinates.latitude, coordinates.longitude], 6);
        mapRef.current.doubleClickZoom.disable();

        window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '', // Remove attribution to hide bottom-right block
        }).addTo(mapRef.current);

        markerRef.current = window.L.marker([coordinates.latitude, coordinates.longitude]).addTo(mapRef.current);

        const handleClick = (e: LeafletMouseEvent) => {
            const newCoords: ICoordinate = {
                latitude: roundToFour(e.latlng.lat),
                longitude: roundToFour(e.latlng.lng),
            };
            onMapClick?.(newCoords);
        };

        mapRef.current.on('click', handleClick);

        return () => {
            mapRef.current?.off('click', handleClick);
            mapRef.current?.remove();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Aggiornamento del marker al variare delle coordinate (provenienti dal componente padre)
    // NON ri-centrare la mappa automaticamente: l'utente deve poter navigare liberamente
    useEffect(() => {
        if (mapRef.current && markerRef.current) {
            markerRef.current.setLatLng([coordinates.latitude, coordinates.longitude]);
            // Delay capture to allow tiles to load
            const captureTimeout = setTimeout(() => {
                getImgFromMap();
            }, 1500);
            return () => clearTimeout(captureTimeout);
        }
    }, [coordinates, getImgFromMap]);

    return <div className="mapContainer" id="map" ref={divMap} />;
});

Map.displayName = 'Map';

export default Map;
